/**
 * @module @repo/global-login-sdk/legal
 * Datenschicht der zentralen Rechtstexte (ADR-0010, Option A „SDK-in-App"): lädt
 * read-only über den publishable Key das Rechtstext-Profil der App (`traits` +
 * `placeholders`), die Standard-Bausteine der Kategorie und die App-eigenen
 * Overrides — und löst daraus mit `resolveDocument` aus `@repo/shared-types` das
 * fertige Dokument auf.
 *
 * Bewusst KEIN eigener Renderer und KEINE eigene Regel-Engine: Regel-Auswertung
 * (`evaluateAppliesWhen`), Platzhalter-Ersetzung (`renderBlock`) und die
 * Pipeline (`resolveDocument`) liegen als SSOT in `@repo/shared-types`, damit
 * Admin-Vorschau und ausgelieferte Seite Zeichen für Zeichen dasselbe zeigen.
 * {@link resolveLegalDocument} ist deshalb reine Verdrahtung: es übersetzt das
 * Query-Ergebnis in die Eingabe von `resolveDocument`.
 *
 * Live-Propagierung: Weil `legal.blocks` bei jedem Laden frisch gelesen wird,
 * zeigt eine App OHNE Override sofort einen geänderten Standard — ohne Deploy,
 * genau wie `brand_tokens` das Login-Theming trägt (ADR-0005).
 */
import { queryOptions } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  LegalAppProfileSchema,
  LegalBlockOverrideSchema,
  LegalBlockSchema,
  resolveDocument,
  type LegalAppProfile,
  type LegalBlock,
  type LegalCategory,
  type LegalOverrideInput,
  type ResolvedLegalDocument,
} from '@repo/shared-types';

// ---------------------------------------------------------------------------
// PostgREST-Selects — snake_case → camelCase, damit die Rohzeile direkt gegen
// das gleichnamige Zod-Schema aus @repo/shared-types validiert werden kann.
// ---------------------------------------------------------------------------

/** `identity.apps` → nur die `id`; der Slug ist der öffentliche Einstieg. */
const APP_ID_SELECT = 'id';

/**
 * `legal.blocks` OHNE `editor_note`: der Verwalter-Hinweis wird laut Datenmodell
 * nie ausgeliefert, also gar nicht erst geholt. `LegalBlockSchema` hat für das
 * Feld einen Default (`null`) — die schmalere Zeile parst deshalb unverändert.
 */
const BLOCK_SELECT =
  'key, category, titleDe:title_de, bodyDe:body_de, appliesWhen:applies_when, ' +
  'required, legalRefs:legal_refs, sort, version';

/** `legal.app_profile` → nur was gerendert wird: Platzhalter-Werte + Merkmale. */
const PROFILE_SELECT = 'placeholders, traits';

/**
 * `legal.app_block_override` → nur die auszuliefernde Fassung. `base_version`
 * und `status` sind Merge-Belange des Admin-Boards (LEG-004) und haben auf der
 * öffentlichen Seite nichts zu suchen.
 */
const OVERRIDE_SELECT = 'blockKey:block_key, bodyDe:body_de';

// ---------------------------------------------------------------------------
// Boundary-Schemata — aus der SSOT abgeleitet (`.pick()`), nie redeklariert
// ---------------------------------------------------------------------------

/** Das gelesene Profil: exakt die Felder aus {@link PROFILE_SELECT}. */
const LegalProfileRowSchema = LegalAppProfileSchema.pick({ placeholders: true, traits: true });

/** Der gelesene Override: exakt die Felder aus {@link OVERRIDE_SELECT}. */
const LegalOverrideRowSchema = LegalBlockOverrideSchema.pick({ blockKey: true, bodyDe: true });

/**
 * Rechtstext-Profil einer App, so wie das SDK es liest: Platzhalter-Werte und
 * Merkmale. `reviewStatus` fehlt bewusst — die RLS liefert öffentlich
 * ausschließlich `published`-Profile aus, ein Client-Filter wäre Theater.
 */
export type LegalProfile = Pick<LegalAppProfile, 'placeholders' | 'traits'>;

/** Rohdaten einer Rechtsseite, wie {@link legalContentQueryOptions} sie liefert. */
export type LegalContent = {
  /** Die geladene Kategorie (= die Seite). */
  readonly category: LegalCategory;
  /** `null`, wenn die App (noch) kein freigegebenes Profil hat. */
  readonly profile: LegalProfile | null;
  /** Standard-Bausteine der Kategorie, ungefiltert (nach `sort`). */
  readonly blocks: readonly LegalBlock[];
  /** App-eigene Fassungen; überschreiben je Baustein den Standard. */
  readonly overrides: readonly LegalOverrideInput[];
};

// ---------------------------------------------------------------------------
// Query-Keys + Cache-Politik
// ---------------------------------------------------------------------------

/** Namensraum aller SDK-Rechtstext-Queries (kollidiert nicht mit App-Keys). */
export const LEGAL_QUERY_KEY_ROOT = 'global-login-legal';

/**
 * Rechtstexte ändern sich selten (Norm-Änderungen, neue Anschrift) — eine
 * Stunde `staleTime` hält die öffentliche Seite schnell, ohne die
 * Live-Propagierung praktisch zu behindern: ein Reload nach Ablauf zeigt den
 * neuen Standard, ein Deploy ist nie nötig.
 */
export const LEGAL_CONTENT_STALE_TIME = 1000 * 60 * 60;

/** Ein Tag im Cache: ein zweiter Besuch der Rechtsseite rendert sofort. */
export const LEGAL_CONTENT_GC_TIME = 1000 * 60 * 60 * 24;

/**
 * Stabiler, kategoriegetrennter Query-Key. Die Kategorie ist Teil des Keys,
 * damit Impressum, Datenschutz und KI-Kennzeichnung getrennte Cache-Einträge
 * haben und sich nie gegenseitig überschreiben.
 *
 * @param slug App-Slug aus `identity.apps.slug`.
 * @param category Die Rechtsseite (`impressum` | `datenschutz` | `ki`).
 * @returns Der Query-Key als `const`-Tupel.
 */
export function legalContentQueryKey(
  slug: string,
  category: LegalCategory,
): readonly [typeof LEGAL_QUERY_KEY_ROOT, 'content', string, LegalCategory] {
  return [LEGAL_QUERY_KEY_ROOT, 'content', slug, category] as const;
}

// ---------------------------------------------------------------------------
// Laden
// ---------------------------------------------------------------------------

/**
 * Anzeigbarer Fehlertext. Bewusst ohne die rohe PostgREST-/Zod-Meldung: die
 * würde Schema-, Tabellen- und Spaltennamen auf eine öffentliche Seite tragen
 * (dasselbe Leak-Argument wie in `request-access`, MED-1).
 */
const LOAD_FAILED_MESSAGE = 'Die Rechtstexte konnten nicht geladen werden.';

/**
 * Loggt die rohe Ursache zur Diagnose und wirft einen nicht-leakenden Fehler.
 * Die Ursache bleibt als `cause` erhalten, damit sie in DevTools/Sentry sichtbar
 * ist, ohne im DOM zu landen.
 */
function failLoad(context: string, cause: unknown): never {
  console.error(`[global-login-sdk] legal: ${context}`, cause);
  throw new Error(LOAD_FAILED_MESSAGE, { cause });
}

/**
 * Löst den App-Slug in die `identity.apps.id` auf — der Schlüssel, unter dem
 * Profil und Overrides liegen. BEWUSST ohne `active = true`-Filter: eine
 * vorübergehend deaktivierte App-Zeile darf ein noch erreichbares Impressum
 * nicht abschalten (§ 5 DDG „ständig verfügbar").
 */
async function fetchAppId(
  client: SupabaseClient,
  slug: string,
  signal: AbortSignal,
): Promise<string> {
  const { data, error } = await client
    .schema('identity')
    .from('apps')
    .select(APP_ID_SELECT)
    .eq('slug', slug)
    .abortSignal(signal)
    .maybeSingle();
  if (error) failLoad(`Slug '${slug}' konnte nicht aufgelöst werden`, error);
  if (!data) failLoad(`Slug '${slug}' ist nicht registriert`, null);
  // Der Typ der App-id ist in der SSOT definiert (app_profile.appId) — keine
  // zweite, lokale uuid-Deklaration.
  return LegalAppProfileSchema.shape.appId.parse((data as { id: unknown }).id);
}

/** Lädt die Standard-Bausteine einer Kategorie, nach `sort` sortiert. */
async function fetchBlocks(
  client: SupabaseClient,
  category: LegalCategory,
  signal: AbortSignal,
): Promise<LegalBlock[]> {
  const { data, error } = await client
    .schema('legal')
    .from('blocks')
    .select(BLOCK_SELECT)
    .eq('category', category)
    .order('sort')
    .abortSignal(signal);
  if (error) failLoad(`Bausteine '${category}' konnten nicht geladen werden`, error);
  return LegalBlockSchema.array().parse(data ?? []);
}

/** Lädt das Rechtstext-Profil der App (`null`, wenn nicht freigegeben). */
async function fetchProfile(
  client: SupabaseClient,
  appId: string,
  signal: AbortSignal,
): Promise<LegalProfile | null> {
  const { data, error } = await client
    .schema('legal')
    .from('app_profile')
    .select(PROFILE_SELECT)
    .eq('app_id', appId)
    .abortSignal(signal)
    .maybeSingle();
  if (error) failLoad('Profil konnte nicht geladen werden', error);
  return data ? LegalProfileRowSchema.parse(data) : null;
}

/** Lädt die App-eigenen Fassungen (Overrides) der App. */
async function fetchOverrides(
  client: SupabaseClient,
  appId: string,
  signal: AbortSignal,
): Promise<LegalOverrideInput[]> {
  const { data, error } = await client
    .schema('legal')
    .from('app_block_override')
    .select(OVERRIDE_SELECT)
    .eq('app_id', appId)
    .abortSignal(signal);
  if (error) failLoad('Overrides konnten nicht geladen werden', error);
  return LegalOverrideRowSchema.array().parse(data ?? []);
}

/**
 * Lädt alle Rohdaten einer Rechtsseite in zwei Wellen: Slug-Auflösung und
 * Baustein-Katalog laufen parallel (der Katalog hängt nicht an der App), danach
 * parallel Profil und Overrides (beide brauchen die `app_id`).
 */
async function fetchLegalContent(
  client: SupabaseClient,
  slug: string,
  category: LegalCategory,
  signal: AbortSignal,
): Promise<LegalContent> {
  try {
    const [appId, blocks] = await Promise.all([
      fetchAppId(client, slug, signal),
      fetchBlocks(client, category, signal),
    ]);
    const [profile, overrides] = await Promise.all([
      fetchProfile(client, appId, signal),
      fetchOverrides(client, appId, signal),
    ]);
    return { category, profile, blocks, overrides };
  } catch (cause) {
    // Abgebrochen (Unmount, Navigation): unverändert weiterwerfen. TanStack
    // erkennt den Abbruch selbst — ihn als Ladefehler zu loggen wäre Rauschen.
    if (signal.aborted) throw cause;
    // Zod-Parse-Fehler (unerwartete Zeilenform) landen hier und werden genauso
    // nicht-leakend behandelt wie PostgREST-Fehler. Ein bereits übersetzter
    // Fehler wird nicht doppelt verpackt.
    if (cause instanceof Error && cause.message === LOAD_FAILED_MESSAGE) throw cause;
    return failLoad(`Rechtstexte '${category}' für '${slug}'`, cause);
  }
}

/**
 * `queryOptions`-Factory für eine Rechtsseite. Liest read-only über den
 * publishable Key (RLS: `anon` + `authenticated`, bei Profil/Overrides nur
 * `published`) und validiert jede Zeile am Boundary gegen die Schemata aus
 * `@repo/shared-types`.
 *
 * `networkMode: 'always'` ist Pflicht, nicht Geschmack: Automations-/Headless-
 * Browser melden `navigator.onLine === false`, wodurch die Query dauerhaft in
 * `fetchStatus: 'paused'` hängen bliebe — das Impressum würde nie erscheinen.
 *
 * @param client Der Supabase-Client der App (siehe `createGlobalLoginClient`).
 * @param slug App-Slug aus `identity.apps.slug` (z. B. `flammenreiter-lore`).
 * @param category Die Rechtsseite (`impressum` | `datenschutz` | `ki`).
 * @returns Typisierte `queryOptions`, die {@link LegalContent} liefern.
 */
export function legalContentQueryOptions(
  client: SupabaseClient,
  slug: string,
  category: LegalCategory,
) {
  return queryOptions<LegalContent>({
    queryKey: legalContentQueryKey(slug, category),
    queryFn: ({ signal }) => fetchLegalContent(client, slug, category, signal),
    staleTime: LEGAL_CONTENT_STALE_TIME,
    gcTime: LEGAL_CONTENT_GC_TIME,
    refetchOnWindowFocus: false,
    retry: 1,
    networkMode: 'always',
  });
}

// ---------------------------------------------------------------------------
// Auflösung
// ---------------------------------------------------------------------------

/**
 * Löst die geladenen Rohdaten zum fertigen Dokument auf — Schritte 2–6 der
 * Render-Pipeline (filtern per `applies_when`, nach `sort` ordnen, je Baustein
 * Override ODER Standard wählen, Platzhalter + `<!-- optional: … -->` rendern).
 *
 * Reine Verdrahtung auf `resolveDocument` aus `@repo/shared-types`: ein zweiter
 * Renderer im SDK wäre genau die Drift zwischen Admin-Vorschau und
 * ausgelieferter Seite, die ADR-0010 abschafft.
 *
 * EINE Auslieferungs-Sperre kommt hinzu (kein Rendering, sondern eine
 * Freigabe-Entscheidung): ohne sichtbares Profil (`profile === null`, die RLS
 * zeigt öffentlich nur `published`) wird NICHTS ausgeliefert. Sonst rendert ein
 * `applies_when: 'true'`-Baustein wie `datenschutz.verantwortlicher` eine
 * Rechtsseite voller „[Angabe fehlt: …]" — ein unvollständiger Rechtstext ist
 * schlechter als der sichtbare Hinweis „noch nicht freigegeben".
 *
 * @param content Das Ergebnis von {@link legalContentQueryOptions}.
 * @returns Die gerenderten Bausteine samt `missing`-Platzhaltern und Warnungen.
 */
export function resolveLegalDocument(content: LegalContent): ResolvedLegalDocument {
  if (!content.profile) {
    return {
      blocks: [],
      text: '',
      missing: [],
      warnings: [
        `kein freigegebenes Rechtstext-Profil für '${content.category}' — ` +
          `es wird nichts ausgeliefert`,
      ],
    };
  }
  return resolveDocument({
    blocks: content.blocks,
    traits: content.profile?.traits ?? {},
    placeholders: content.profile?.placeholders ?? {},
    overrides: content.overrides,
    category: content.category,
  });
}
